import React, { useEffect, useState } from 'react';
import { projectsService, Project, CreateProjectData } from '../services/projects';

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProject, setNewProject] = useState<CreateProjectData>({
    name: '',
    description: '',
    topic: 'ML',
    difficulty: 'Beginner',
    status: 'Planning',
  });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const allProjects = await projectsService.getAll();
      setProjects(allProjects);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProject = async () => {
    if (!newProject.name) return;
    
    try {
      await projectsService.create(newProject);
      setNewProject({
        name: '',
        description: '',
        topic: 'ML',
        difficulty: 'Beginner',
        status: 'Planning',
      });
      setShowAddForm(false);
      loadProjects();
    } catch (error) {
      console.error('Error adding project:', error);
      alert('Failed to add project. Please try again.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Complete':
      case 'Published':
        return 'bg-green-100 text-green-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      case 'Planning':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTopicColor = (topic: string) => {
    const colors: Record<string, string> = {
      ML: 'bg-blue-100 text-blue-800',
      CV: 'bg-purple-100 text-purple-800',
      QC: 'bg-indigo-100 text-indigo-800',
      QML: 'bg-pink-100 text-pink-800',
      Security: 'bg-red-100 text-red-800',
      Backend: 'bg-gray-100 text-gray-800',
      Integrated: 'bg-green-100 text-green-800',
    };
    return colors[topic] || 'bg-gray-100 text-gray-800';
  };

  const getPhaseProjects = () => {
    return {
      phase1: projects.filter(p => ['ML', 'CV'].includes(p.topic)),
      phase2: projects.filter(p => ['QC', 'QML'].includes(p.topic)),
      phase3: projects.filter(p => ['Security', 'Integrated'].includes(p.topic)),
    };
  };

  if (loading) {
    return <div className="p-6">Loading projects...</div>;
  }

  const phaseProjects = getPhaseProjects();
  const completedProjects = projects.filter(p => p.status === 'Complete' || p.status === 'Published').length;
  const goal = 8;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Portfolio Projects
            </h1>
            <p className="text-gray-600">
              Target: 8-10 impactful projects by August 2027
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            {showAddForm ? 'Cancel' : '+ New Project'}
          </button>
        </div>

        {/* Progress Overview */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl font-semibold">Project Portfolio Progress</h2>
              <p className="text-gray-600">{completedProjects} / {goal} projects completed</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Total Projects</p>
              <p className="text-3xl font-bold text-primary">{projects.length}</p>
            </div>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className="bg-secondary h-4 rounded-full transition-all duration-300"
              style={{ width: `${Math.min((completedProjects / goal) * 100, 100)}%` }}
            />
          </div>
        </div>

        {/* Add Project Form */}
        {showAddForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-xl font-semibold mb-4">Create New Project</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Project Name</label>
                <input
                  type="text"
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Vision Transformer Image Classifier"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                  placeholder="Project description..."
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Topic</label>
                  <select
                    value={newProject.topic}
                    onChange={(e) => setNewProject({ ...newProject, topic: e.target.value as any })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="ML">ML</option>
                    <option value="CV">CV</option>
                    <option value="QC">QC</option>
                    <option value="QML">QML</option>
                    <option value="Security">Security</option>
                    <option value="Backend">Backend</option>
                    <option value="Integrated">Integrated</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Difficulty</label>
                  <select
                    value={newProject.difficulty}
                    onChange={(e) => setNewProject({ ...newProject, difficulty: e.target.value as any })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <select
                    value={newProject.status}
                    onChange={(e) => setNewProject({ ...newProject, status: e.target.value as any })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="Planning">Planning</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Complete">Complete</option>
                    <option value="Published">Published</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">GitHub Link (optional)</label>
                  <input
                    type="text"
                    value={newProject.github_link || ''}
                    onChange={(e) => setNewProject({ ...newProject, github_link: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://github.com/..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Live Link (optional)</label>
                  <input
                    type="text"
                    value={newProject.live_link || ''}
                    onChange={(e) => setNewProject({ ...newProject, live_link: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://..."
                  />
                </div>
              </div>
              <button
                onClick={handleAddProject}
                className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
              >
                Create Project
              </button>
            </div>
          </div>
        )}

        {/* Project Phases */}
        <div className="space-y-6">
          {/* Phase 1: ML/CV */}
          <div>
            <h2 className="text-2xl font-semibold mb-4">
              Phase 1: ML/CV Foundation (Dec 2025 - Jun 2026)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {phaseProjects.phase1.map((project) => (
                <div key={project.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold">{project.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{project.description}</p>
                  <div className="flex gap-2 mb-3">
                    <span className={`px-2 py-1 rounded text-xs ${getTopicColor(project.topic)}`}>
                      {project.topic}
                    </span>
                    <span className="px-2 py-1 rounded text-xs bg-gray-100">
                      {project.difficulty}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm text-gray-600 mb-3">
                    <span>{project.hours_spent.toFixed(1)}h spent</span>
                    {project.start_date && (
                      <span>{new Date(project.start_date).toLocaleDateString()}</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {project.github_link && (
                      <a
                        href={project.github_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline text-sm"
                      >
                        GitHub →
                      </a>
                    )}
                    {project.live_link && (
                      <a
                        href={project.live_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:underline text-sm"
                      >
                        Live Demo →
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Phase 2: Quantum */}
          <div>
            <h2 className="text-2xl font-semibold mb-4">
              Phase 2: Quantum Computing (Jul 2026 - Jan 2027)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {phaseProjects.phase2.map((project) => (
                <div key={project.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold">{project.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{project.description}</p>
                  <div className="flex gap-2 mb-3">
                    <span className={`px-2 py-1 rounded text-xs ${getTopicColor(project.topic)}`}>
                      {project.topic}
                    </span>
                    <span className="px-2 py-1 rounded text-xs bg-gray-100">
                      {project.difficulty}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm text-gray-600 mb-3">
                    <span>{project.hours_spent.toFixed(1)}h spent</span>
                    {project.start_date && (
                      <span>{new Date(project.start_date).toLocaleDateString()}</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {project.github_link && (
                      <a
                        href={project.github_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline text-sm"
                      >
                        GitHub →
                      </a>
                    )}
                    {project.live_link && (
                      <a
                        href={project.live_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:underline text-sm"
                      >
                        Live Demo →
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Phase 3: Integrated */}
          <div>
            <h2 className="text-2xl font-semibold mb-4">
              Phase 3: Integrated Projects (Feb 2027 - Aug 2027)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {phaseProjects.phase3.map((project) => (
                <div key={project.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold">{project.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{project.description}</p>
                  <div className="flex gap-2 mb-3">
                    <span className={`px-2 py-1 rounded text-xs ${getTopicColor(project.topic)}`}>
                      {project.topic}
                    </span>
                    <span className="px-2 py-1 rounded text-xs bg-gray-100">
                      {project.difficulty}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm text-gray-600 mb-3">
                    <span>{project.hours_spent.toFixed(1)}h spent</span>
                    {project.start_date && (
                      <span>{new Date(project.start_date).toLocaleDateString()}</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {project.github_link && (
                      <a
                        href={project.github_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline text-sm"
                      >
                        GitHub →
                      </a>
                    )}
                    {project.live_link && (
                      <a
                        href={project.live_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:underline text-sm"
                      >
                        Live Demo →
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Projects;

